import { ENEMY_COLORS } from './constants.js';
import { getFaction, pickEnemyVariant, pickEnemyColor, combatScale } from './space/factions.js';
import { renderQuality } from './renderQuality.js';

/** All combat / visual enemy classes */
export const ENEMY_VARIANTS = [
    'swarm', 'brute', 'spiker', 'orbiter',
    'interceptor', 'bomber', 'phantom', 'saucer', 'mine',
];

const SHOOTERS = new Set(['orbiter', 'bomber', 'saucer', 'boss']);

export class Enemy {
    constructor(x, y, theme = null) {
        this.reset(x, y, theme);
    }

    /**
     * @param {number} x
     * @param {number} y
     * @param {{ faction?: object, danger?: number, type?: string, variant?: string, color?: string }|null} theme
     */
    reset(x, y, theme = null) {
        this.x = x;
        this.y = y;
        this.isBoss = false;
        this.shootCooldown = 0;
        this.shootInterval = 120 + Math.random() * 60;
        this.shootRange = 300;
        this.frozenUntil = null;
        this.pulsePhase = Math.random() * Math.PI * 2;
        this.hitFlash = 0;
        this.spin = Math.random() * Math.PI * 2;
        this.angle = Math.random() * Math.PI * 2;
        this._prevAngle = this.angle;
        this.bank = 0;
        this.animT = Math.random() * 1000;
        this.thrust = 0;
        this.trail = [];
        this.blink = Math.random() * Math.PI * 2;
        this.dashTimer = 0;
        this.phase = 0; // phantom cloak 0..1
        this.chargeTimer = 0; // mine charge windup
        this.alive = true;

        const faction = theme?.faction || getFaction(theme?.type) || getFaction('barren');
        const danger = theme?.danger ?? 0.5;
        this.faction = faction;
        this.enemyShip = faction.enemyShip || 'drone';
        this.form = faction.form || 'scrap';

        this.variant = theme?.variant
            || pickEnemyVariant(faction)
            || ENEMY_VARIANTS[Math.floor(Math.random() * ENEMY_VARIANTS.length)];
        this.color = theme?.color || pickEnemyColor(faction) || ENEMY_COLORS[0];

        this._applyVariantStats();
        const scale = combatScale(faction, danger);
        this.speed *= scale.speedMul;
        this.hp = Math.round(this.hp * scale.hpMul);
        this.maxHp = this.hp;
        this.damage = Math.round(this.damage * scale.dmgMul);
    }

    _applyVariantStats() {
        switch (this.variant) {
            case 'brute':
                this.radius = 20;
                this.speed = 0.72 + Math.random() * 0.22;
                this.hp = 58;
                this.maxHp = 58;
                this.damage = 14;
                this.hasPulse = false;
                this.shipSilhouette = 'brute';
                break;
            case 'spiker':
                this.radius = 13;
                this.speed = 1.4 + Math.random() * 0.35;
                this.hp = 22;
                this.maxHp = 22;
                this.damage = 12;
                this.hasPulse = true;
                this.shipSilhouette = 'spiker';
                break;
            case 'orbiter':
                this.radius = 15;
                this.speed = 1.0 + Math.random() * 0.28;
                this.hp = 34;
                this.maxHp = 34;
                this.damage = 10;
                this.hasPulse = true;
                this.shootInterval = 85 + Math.random() * 40;
                this.shootRange = 320;
                this.shipSilhouette = 'orbiter';
                break;
            case 'interceptor':
                this.radius = 11;
                this.speed = 1.85 + Math.random() * 0.4;
                this.hp = 18;
                this.maxHp = 18;
                this.damage = 9;
                this.hasPulse = true;
                this.dashTimer = 40 + Math.random() * 50;
                this.shipSilhouette = 'interceptor';
                break;
            case 'bomber':
                this.radius = 18;
                this.speed = 0.65 + Math.random() * 0.2;
                this.hp = 48;
                this.maxHp = 48;
                this.damage = 16;
                this.hasPulse = true;
                this.shootInterval = 110 + Math.random() * 50;
                this.shootRange = 360;
                this.shipSilhouette = 'bomber';
                break;
            case 'phantom':
                this.radius = 14;
                this.speed = 1.15 + Math.random() * 0.3;
                this.hp = 26;
                this.maxHp = 26;
                this.damage = 11;
                this.hasPulse = true;
                this.phase = Math.random();
                this.shipSilhouette = 'phantom';
                break;
            case 'saucer':
                this.radius = 16;
                this.speed = 0.95 + Math.random() * 0.25;
                this.hp = 40;
                this.maxHp = 40;
                this.damage = 11;
                this.hasPulse = true;
                this.shootInterval = 70 + Math.random() * 35;
                this.shootRange = 340;
                this.shipSilhouette = 'saucer';
                break;
            case 'mine':
                this.radius = 12;
                this.speed = 0.45 + Math.random() * 0.15;
                this.hp = 30;
                this.maxHp = 30;
                this.damage = 18;
                this.hasPulse = true;
                this.chargeTimer = 0;
                this.shipSilhouette = 'mine';
                break;
            default: // swarm
                this.radius = 11;
                this.speed = 1.3 + Math.random() * 0.45;
                this.hp = 22;
                this.maxHp = 22;
                this.damage = 8;
                this.hasPulse = Math.random() < 0.55;
                this.shipSilhouette = 'swarm';
                break;
        }
    }

    update(player) {
        if (this.frozenUntil && Date.now() < this.frozenUntil) {
            this.animT += 0.08;
            this._updateTrail(false);
            return;
        }

        const dx = player.x - this.x;
        const dy = player.y - this.y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;
        const t = Date.now();
        this.animT += 0.12 + this.speed * 0.04;
        this.blink += 0.15;

        // Desired face angle
        let face = Math.atan2(dy, dx);
        let ox = 0, oy = 0;
        let spd = this.speed;

        switch (this.variant) {
            case 'spiker': {
                const side = Math.sin(t / 160 + this.pulsePhase) * 0.65;
                ox = (-dy / dist) * side;
                oy = (dx / dist) * side;
                this.spin += 0.14;
                break;
            }
            case 'interceptor': {
                this.dashTimer--;
                const weave = Math.sin(t / 120 + this.pulsePhase) * 0.9;
                ox = (-dy / dist) * weave;
                oy = (dx / dist) * weave;
                if (this.dashTimer <= 0) {
                    spd *= 2.4;
                    this.dashTimer = 50 + Math.random() * 70;
                    this.thrust = 1.4;
                } else {
                    this.thrust = 0.7 + Math.sin(this.animT * 2) * 0.2;
                }
                break;
            }
            case 'bomber': {
                // Keep mid-range if possible
                if (dist < 160) {
                    ox = -dx / dist;
                    oy = -dy / dist;
                    face = Math.atan2(-dy, -dx);
                } else if (dist > 340) {
                    ox = dx / dist * 0.3;
                    oy = dy / dist * 0.3;
                } else {
                    // Circle
                    ox = (-dy / dist) * 0.7;
                    oy = (dx / dist) * 0.7;
                }
                this.thrust = 0.5 + Math.sin(this.animT) * 0.15;
                break;
            }
            case 'phantom': {
                this.phase = (Math.sin(t / 400 + this.pulsePhase) + 1) * 0.5;
                // Rush when solid
                if (this.phase > 0.55) spd *= 1.35;
                else spd *= 0.55;
                const side = Math.sin(t / 200 + this.pulsePhase) * 0.4;
                ox = (-dy / dist) * side;
                oy = (dx / dist) * side;
                this.spin += 0.08;
                break;
            }
            case 'saucer': {
                this.spin += 0.1;
                // Orbit-ish strafe
                ox = (-dy / dist) * 0.85;
                oy = (dx / dist) * 0.85;
                if (dist > 220) {
                    ox += dx / dist * 0.4;
                    oy += dy / dist * 0.4;
                }
                this.thrust = 0.45 + Math.sin(this.animT * 1.5) * 0.2;
                break;
            }
            case 'mine': {
                this.spin += 0.06;
                if (dist < 200) {
                    this.chargeTimer = Math.min(40, this.chargeTimer + 1);
                    spd = this.speed * (1.2 + this.chargeTimer * 0.06);
                    this.hasPulse = true;
                } else {
                    this.chargeTimer = Math.max(0, this.chargeTimer - 0.5);
                    // Drift wobble
                    ox = Math.sin(t / 300 + this.pulsePhase) * 0.5;
                    oy = Math.cos(t / 280 + this.pulsePhase) * 0.5;
                }
                break;
            }
            case 'orbiter': {
                // Prefer range, circle when close
                if (dist < 180) {
                    ox = -dx / dist * 0.5 + (-dy / dist) * 0.8;
                    oy = -dy / dist * 0.5 + (dx / dist) * 0.8;
                }
                this.spin += 0.05;
                this.thrust = 0.55 + Math.sin(this.animT) * 0.2;
                break;
            }
            case 'brute': {
                this.spin += 0.02;
                this.thrust = 0.35 + Math.sin(this.animT * 0.8) * 0.1;
                break;
            }
            default: {
                const side = Math.sin(t / 220 + this.pulsePhase) * 0.35;
                ox = (-dy / dist) * side;
                oy = (dx / dist) * side;
                this.thrust = 0.65 + Math.sin(this.animT * 2.2) * 0.25;
                break;
            }
        }

        // Smooth face + bank
        let ad = face - this.angle;
        while (ad > Math.PI) ad -= Math.PI * 2;
        while (ad < -Math.PI) ad += Math.PI * 2;
        this.angle += ad * 0.18;
        const turn = this.angle - this._prevAngle;
        this._prevAngle = this.angle;
        this.bank += (-turn * 12 - this.bank) * 0.15;

        // Move
        const mx = dx / dist + ox;
        const my = dy / dist + oy;
        const mlen = Math.hypot(mx, my) || 1;
        this.x += (mx / mlen) * spd;
        this.y += (my / mlen) * spd;

        if (this.hitFlash > 0) this.hitFlash--;
        if (this.shootCooldown > 0) this.shootCooldown--;
        if (this.thrust > 0.05) this.thrust *= 0.96;

        this._updateTrail(true);
    }

    _updateTrail(moving) {
        const q = renderQuality();
        const cap = q === 'high' ? 8 : q === 'medium' ? 3 : 0;
        if (cap === 0) {
            if (this.trail.length) this.trail.length = 0;
            return;
        }
        if (moving && (this.thrust > 0.2 || this.speed > 0.8)) {
            const back = this.angle + Math.PI;
            const bx = this.x + Math.cos(back) * this.radius * 0.7;
            const by = this.y + Math.sin(back) * this.radius * 0.7;
            let slot = this.trail.length >= cap ? this.trail.shift() : null;
            if (!slot) slot = { x: 0, y: 0, life: 0, max: 12, r: 3 };
            slot.x = bx;
            slot.y = by;
            slot.life = q === 'high' ? 8 + Math.random() * 6 : 6;
            slot.max = 12;
            slot.r = q === 'high' ? 2 + Math.random() * 3 : 2.5;
            this.trail.push(slot);
        }
        for (let i = this.trail.length - 1; i >= 0; i--) {
            this.trail[i].life--;
            if (this.trail[i].life <= 0) this.trail.splice(i, 1);
        }
    }

    canShoot(player) {
        if (this.shootCooldown > 0) return false;
        if (!this.isBoss && !SHOOTERS.has(this.variant)) return false;
        // Phantoms only fire when mostly visible
        if (this.variant === 'phantom' && this.phase < 0.45) return false;

        const dx = player.x - this.x;
        const dy = player.y - this.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        return dist < this.shootRange;
    }

    shoot(player) {
        if (!this.canShoot(player)) return null;

        const dx = player.x - this.x;
        const dy = player.y - this.y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;

        this.shootCooldown = this.shootInterval;

        let bulletSpeed = 3;
        let dmgMul = 0.5;
        if (this.variant === 'bomber') {
            bulletSpeed = 2.4;
            dmgMul = 0.75;
        } else if (this.variant === 'saucer') {
            bulletSpeed = 3.4;
            dmgMul = 0.45;
        }

        return {
            x: this.x,
            y: this.y,
            vx: (dx / dist) * bulletSpeed,
            vy: (dy / dist) * bulletSpeed,
            damage: this.damage * dmgMul,
            color: this.color,
            isEnemyProjectile: true,
        };
    }

    draw(ctx) {
        ctx.save();
        const t = Date.now();
        let glowSize = 16;
        let drawRadius = this.radius;
        const bodyColor = this.hitFlash > 0 ? '#ffffff' : this.color;

        // Phantom cloaking
        let alpha = 1;
        if (this.variant === 'phantom') {
            alpha = 0.28 + this.phase * 0.72;
        }

        if (this.hasPulse) {
            const pulse = Math.sin(t / 260 + this.pulsePhase);
            glowSize = 16 + Math.abs(pulse) * 10;
            drawRadius = this.radius * (1 + pulse * 0.07);
        }
        if (this.variant === 'mine' && this.chargeTimer > 10) {
            glowSize += this.chargeTimer * 0.4;
        }

        const q = renderQuality();

        // Engine trail. Gradients per puff are too expensive once a wave is on screen.
        if (q === 'high') {
            for (let ti = 0; ti < this.trail.length; ti++) {
                const p = this.trail[ti];
                const a = (p.life / p.max) * 0.7 * alpha;
                const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.r);
                g.addColorStop(0, `rgba(255,220,140,${a})`);
                g.addColorStop(1, 'transparent');
                ctx.fillStyle = g;
                ctx.beginPath();
                ctx.arc(p.x, p.y, p.r * (1.2 - p.life / p.max), 0, Math.PI * 2);
                ctx.fill();
            }
        } else if (this.trail.length) {
            ctx.globalAlpha = 0.35 * alpha;
            ctx.fillStyle = this.color;
            ctx.beginPath();
            for (let ti = 0; ti < this.trail.length; ti++) {
                const p = this.trail[ti];
                ctx.moveTo(p.x + p.r, p.y);
                ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
            }
            ctx.fill();
        }

        ctx.globalAlpha = alpha;

        if (q === 'high') {
            const amb = ctx.createRadialGradient(this.x, this.y, 0, this.x, this.y, drawRadius * 2.5);
            amb.addColorStop(0, bodyColor + '55');
            amb.addColorStop(1, 'transparent');
            ctx.fillStyle = amb;
            ctx.beginPath();
            ctx.arc(this.x, this.y, drawRadius * 2.5, 0, Math.PI * 2);
            ctx.fill();
            ctx.shadowBlur = glowSize;
            ctx.shadowColor = this.color;
        } else if (q === 'medium') {
            ctx.globalAlpha = alpha * 0.16;
            ctx.fillStyle = bodyColor;
            ctx.beginPath();
            ctx.arc(this.x, this.y, drawRadius * 1.5, 0, Math.PI * 2);
            ctx.fill();
            ctx.globalAlpha = alpha;
        }

        ctx.save();
        ctx.translate(this.x, this.y);

        const sil = this.shipSilhouette || this.variant;
        const spinForms = sil === 'spiker' || sil === 'saucer' || sil === 'mine' || sil === 'orbiter'
            || this.enemyShip === 'shard' || this.enemyShip === 'prism';

        if (spinForms && sil !== 'interceptor' && sil !== 'swarm' && sil !== 'bomber') {
            ctx.rotate(this.spin);
        } else {
            ctx.rotate(this.angle + Math.PI / 2);
            // Bank squash
            const sx = Math.max(0.55, Math.cos(this.bank * 0.9));
            ctx.scale(sx, 1 + Math.abs(this.bank) * 0.08);
        }

        // Thruster flame (behind ship in local space)
        if (sil !== 'mine' && sil !== 'saucer') {
            this._drawThruster(ctx, drawRadius, bodyColor, t);
        }

        this._drawByVariant(ctx, drawRadius, bodyColor, t, sil);

        // Nav blink lights
        if (this.hitFlash <= 0 && sil !== 'mine') {
            const blink = Math.sin(this.blink) > 0.65;
            if (blink) {
                ctx.fillStyle = '#ffffff';
                ctx.shadowBlur = 8;
                ctx.shadowColor = '#fff';
                ctx.beginPath();
                ctx.arc(drawRadius * 0.35, -drawRadius * 0.1, 1.6, 0, Math.PI * 2);
                ctx.fill();
                ctx.beginPath();
                ctx.arc(-drawRadius * 0.35, -drawRadius * 0.1, 1.6, 0, Math.PI * 2);
                ctx.fill();
            }
        }

        ctx.restore();

        // Outer pulse ring
        if (this.hasPulse) {
            const ringAlpha = (Math.sin(t / 280 + this.pulsePhase) * 0.5 + 0.5) * 0.35 * alpha;
            ctx.globalAlpha = ringAlpha;
            ctx.strokeStyle = this.color;
            ctx.lineWidth = 2;
            ctx.shadowBlur = 0;
            ctx.beginPath();
            ctx.arc(this.x, this.y, drawRadius + 6 + (this.chargeTimer || 0) * 0.15, 0, Math.PI * 2);
            ctx.stroke();
        }

        ctx.globalAlpha = 1;

        // HP bar
        if (this.hp < this.maxHp) {
            this._drawHpBar(ctx, this.radius * 2.2, 4, this.y - this.radius - 12);
        }

        ctx.restore();
    }

    _drawThruster(ctx, r, color, t) {
        const thrust = 0.45 + this.thrust * 0.55 + Math.sin(this.animT * 3) * 0.12;
        const len = r * (0.55 + thrust * 0.9);
        if (renderQuality() !== 'high') {
            ctx.fillStyle = color;
            ctx.beginPath();
            ctx.moveTo(-r * 0.22, r * 0.25);
            ctx.lineTo(0, r * 0.25 + len);
            ctx.lineTo(r * 0.22, r * 0.25);
            ctx.closePath();
            ctx.fill();
            return;
        }
        const g = ctx.createLinearGradient(0, r * 0.2, 0, r * 0.2 + len);
        g.addColorStop(0, '#ffffff');
        g.addColorStop(0.25, '#aaddff');
        g.addColorStop(0.55, color);
        g.addColorStop(1, 'transparent');
        ctx.shadowBlur = 10;
        ctx.shadowColor = color;
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.moveTo(-r * 0.22, r * 0.25);
        ctx.lineTo(0, r * 0.25 + len);
        ctx.lineTo(r * 0.22, r * 0.25);
        ctx.closePath();
        ctx.fill();
        // Secondary flicker
        if (Math.sin(t / 40 + this.pulsePhase) > 0) {
            ctx.globalAlpha = 0.5;
            ctx.beginPath();
            ctx.moveTo(-r * 0.1, r * 0.3);
            ctx.lineTo(0, r * 0.3 + len * 0.7);
            ctx.lineTo(r * 0.1, r * 0.3);
            ctx.closePath();
            ctx.fillStyle = '#fff';
            ctx.fill();
            ctx.globalAlpha = 1;
        }
    }

    _drawByVariant(ctx, r, color, t, sil) {
        // Prefer faction ship for visual planet flavor, with variant overrides for specials
        if (sil === 'interceptor') {
            this._drawInterceptor(ctx, r, color, t);
            return;
        }
        if (sil === 'bomber') {
            this._drawBomber(ctx, r, color, t);
            return;
        }
        if (sil === 'phantom') {
            this._drawPhantom(ctx, r, color, t);
            return;
        }
        if (sil === 'saucer') {
            this._drawSaucer(ctx, r, color, t);
            return;
        }
        if (sil === 'mine') {
            this._drawMine(ctx, r, color, t);
            return;
        }
        if (sil === 'brute') {
            this._drawBrute(ctx, r, color);
            return;
        }
        if (sil === 'spiker') {
            this._drawSpiker(ctx, r, color, t);
            return;
        }
        if (sil === 'orbiter') {
            this._drawOrbiter(ctx, r, color, t);
            return;
        }

        // Faction silhouette for swarms / default
        this._drawFactionShip(ctx, r, color, t, this.enemyShip || 'drone');
    }

    /** Planet-themed hostile silhouettes */
    _drawFactionShip(ctx, r, color, t, ship) {
        switch (ship) {
            case 'scavenger':
            case 'block':
                this._drawBlockShip(ctx, r, color);
                break;
            case 'shard':
            case 'prism':
                this._drawShardShip(ctx, r, color, t);
                break;
            case 'blade':
            case 'bolt':
                this._drawBladeShip(ctx, r, color);
                break;
            case 'ring':
                this._drawRingShip(ctx, r, color, t);
                break;
            case 'spiker':
                this._drawSpiker(ctx, r, color, t);
                break;
            case 'fin':
                this._drawFinShip(ctx, r, color);
                break;
            case 'leaf':
                this._drawLeafShip(ctx, r, color);
                break;
            case 'mech':
                this._drawMechShip(ctx, r, color);
                break;
            case 'drone':
            default:
                this._drawDartHostile(ctx, r, color);
                break;
        }
    }

    _drawInterceptor(ctx, r, color, t) {
        const g = ctx.createLinearGradient(0, -r, 0, r);
        g.addColorStop(0, '#fff');
        g.addColorStop(0.3, color);
        g.addColorStop(1, shade(color, -50));
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.moveTo(0, -r * 1.35);
        ctx.lineTo(r * 0.55, r * 0.15);
        ctx.lineTo(r * 0.9, r * 0.75);
        ctx.lineTo(0, r * 0.2);
        ctx.lineTo(-r * 0.9, r * 0.75);
        ctx.lineTo(-r * 0.55, r * 0.15);
        ctx.closePath();
        ctx.fill();
        // Speed stripes
        ctx.strokeStyle = '#ffffff88';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(-r * 0.15, -r * 0.4);
        ctx.lineTo(-r * 0.15, r * 0.05);
        ctx.moveTo(r * 0.15, -r * 0.4);
        ctx.lineTo(r * 0.15, r * 0.05);
        ctx.stroke();
        // Afterburner glow when dashing
        if (this.thrust > 1) {
            ctx.fillStyle = '#ffffffaa';
            ctx.beginPath();
            ctx.arc(0, r * 0.5, r * 0.2, 0, Math.PI * 2);
            ctx.fill();
        }
    }

    _drawBomber(ctx, r, color, t) {
        ctx.fillStyle = shade(color, -20);
        // Wide body
        ctx.beginPath();
        ctx.moveTo(0, -r * 0.9);
        ctx.lineTo(r * 1.1, r * 0.35);
        ctx.lineTo(r * 0.7, r * 0.85);
        ctx.lineTo(0, r * 0.45);
        ctx.lineTo(-r * 0.7, r * 0.85);
        ctx.lineTo(-r * 1.1, r * 0.35);
        ctx.closePath();
        ctx.fill();
        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        ctx.stroke();
        // Bomb bay doors
        const open = 0.5 + Math.sin(t / 400 + this.pulsePhase) * 0.15;
        ctx.fillStyle = shade(color, -40);
        ctx.fillRect(-r * 0.35, -r * 0.1, r * 0.7, r * 0.55 * open);
        // Engine pods
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.ellipse(-r * 0.55, r * 0.55, r * 0.22, r * 0.18, 0, 0, Math.PI * 2);
        ctx.ellipse(r * 0.55, r * 0.55, r * 0.22, r * 0.18, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = '#fff';
        ctx.beginPath();
        ctx.arc(0, -r * 0.25, r * 0.14, 0, Math.PI * 2);
        ctx.fill();
    }

    _drawPhantom(ctx, r, color, t) {
        const ghost = 0.4 + this.phase * 0.6;
        ctx.globalAlpha = ghost;
        // Ghosted dart with afterimage edges
        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(0, -r * 1.15);
        ctx.lineTo(r * 0.75, r * 0.55);
        ctx.lineTo(0, r * 0.2);
        ctx.lineTo(-r * 0.75, r * 0.55);
        ctx.closePath();
        ctx.stroke();
        ctx.fillStyle = shade(color, -30) + '99';
        ctx.fill();
        // Echo ring
        ctx.globalAlpha = ghost * 0.4;
        ctx.beginPath();
        ctx.arc(0, 0, r * (1.1 + Math.sin(t / 200) * 0.15), 0, Math.PI * 2);
        ctx.stroke();
        ctx.globalAlpha = 1;
        ctx.fillStyle = '#fff';
        ctx.beginPath();
        ctx.arc(0, -r * 0.15, r * 0.12, 0, Math.PI * 2);
        ctx.fill();
    }

    _drawSaucer(ctx, r, color, t) {
        // Disc
        const g = ctx.createRadialGradient(0, 0, 0, 0, 0, r * 1.2);
        g.addColorStop(0, '#fff');
        g.addColorStop(0.35, color);
        g.addColorStop(1, shade(color, -45));
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.ellipse(0, 0, r * 1.25, r * 0.55, 0, 0, Math.PI * 2);
        ctx.fill();
        // Dome
        ctx.fillStyle = shade(color, 30);
        ctx.beginPath();
        ctx.ellipse(0, -r * 0.05, r * 0.55, r * 0.4, 0, Math.PI, 0);
        ctx.fill();
        // Spinning lights
        for (let i = 0; i < 6; i++) {
            const a = this.spin + (i / 6) * Math.PI * 2;
            const on = Math.sin(t / 80 + i) > 0;
            ctx.fillStyle = on ? '#fff' : shade(color, 40);
            ctx.beginPath();
            ctx.arc(Math.cos(a) * r * 0.95, Math.sin(a) * r * 0.38, 2.2, 0, Math.PI * 2);
            ctx.fill();
        }
        // Core
        ctx.fillStyle = '#fff';
        ctx.beginPath();
        ctx.arc(0, 0, r * 0.16, 0, Math.PI * 2);
        ctx.fill();
    }

    _drawMine(ctx, r, color, t) {
        const charge = this.chargeTimer / 40;
        ctx.fillStyle = shade(color, -15);
        ctx.beginPath();
        ctx.arc(0, 0, r * 0.75, 0, Math.PI * 2);
        ctx.fill();
        // Spikes
        const spikes = 8;
        ctx.fillStyle = color;
        for (let i = 0; i < spikes; i++) {
            const a = this.spin + (i / spikes) * Math.PI * 2;
            const tip = r * (1.15 + charge * 0.35);
            ctx.beginPath();
            ctx.moveTo(Math.cos(a - 0.22) * r * 0.55, Math.sin(a - 0.22) * r * 0.55);
            ctx.lineTo(Math.cos(a) * tip, Math.sin(a) * tip);
            ctx.lineTo(Math.cos(a + 0.22) * r * 0.55, Math.sin(a + 0.22) * r * 0.55);
            ctx.closePath();
            ctx.fill();
        }
        // Warning blink when charging
        if (charge > 0.3 && Math.sin(t / (50 - charge * 30)) > 0) {
            ctx.fillStyle = '#ff2244';
            ctx.shadowBlur = 12;
            ctx.shadowColor = '#ff2244';
        } else {
            ctx.fillStyle = '#fff';
        }
        ctx.beginPath();
        ctx.arc(0, 0, r * 0.22, 0, Math.PI * 2);
        ctx.fill();
    }

    _drawDartHostile(ctx, r, color) {
        const g = ctx.createLinearGradient(0, -r, 0, r);
        g.addColorStop(0, '#ffffff');
        g.addColorStop(0.3, color);
        g.addColorStop(1, shade(color, -45));
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.moveTo(0, -r * 1.2);
        ctx.lineTo(r * 0.7, r * 0.55);
        ctx.lineTo(0, r * 0.25);
        ctx.lineTo(-r * 0.7, r * 0.55);
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = '#0a0a14';
        ctx.beginPath();
        ctx.arc(0, -r * 0.15, r * 0.22, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = '#fff';
        ctx.beginPath();
        ctx.arc(0, -r * 0.2, r * 0.08, 0, Math.PI * 2);
        ctx.fill();
    }

    _drawBlockShip(ctx, r, color) {
        ctx.fillStyle = shade(color, -20);
        ctx.fillRect(-r * 0.85, -r * 0.7, r * 1.7, r * 1.4);
        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        ctx.strokeRect(-r * 0.85, -r * 0.7, r * 1.7, r * 1.4);
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.moveTo(0, -r * 1.15);
        ctx.lineTo(r * 0.55, -r * 0.5);
        ctx.lineTo(-r * 0.55, -r * 0.5);
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = shade(color, 30);
        ctx.globalAlpha = 0.5;
        ctx.fillRect(-r * 0.55, -r * 0.2, r * 0.4, r * 0.45);
        ctx.fillRect(r * 0.15, -r * 0.2, r * 0.4, r * 0.45);
        ctx.globalAlpha = 1;
        ctx.fillStyle = '#fff';
        ctx.beginPath();
        ctx.arc(0, -r * 0.15, r * 0.15, 0, Math.PI * 2);
        ctx.fill();
    }

    _drawShardShip(ctx, r, color, t = 0) {
        const pulse = 1 + Math.sin(t / 200 + this.pulsePhase) * 0.05;
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.moveTo(0, -r * 1.3 * pulse);
        ctx.lineTo(r * 0.55, 0);
        ctx.lineTo(0, r * 1.1);
        ctx.lineTo(-r * 0.55, 0);
        ctx.closePath();
        ctx.fill();
        ctx.strokeStyle = '#ffffffcc';
        ctx.lineWidth = 1.5;
        ctx.stroke();
        ctx.fillStyle = '#ffffff88';
        ctx.beginPath();
        ctx.moveTo(0, -r * 0.6);
        ctx.lineTo(r * 0.22, 0);
        ctx.lineTo(0, r * 0.5);
        ctx.lineTo(-r * 0.22, 0);
        ctx.closePath();
        ctx.fill();
    }

    _drawBladeShip(ctx, r, color) {
        const g = ctx.createLinearGradient(0, -r, 0, r);
        g.addColorStop(0, '#fff');
        g.addColorStop(0.4, color);
        g.addColorStop(1, shade(color, -50));
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.moveTo(0, -r * 1.4);
        ctx.lineTo(r * 0.35, r * 0.2);
        ctx.lineTo(r * 0.15, r * 0.9);
        ctx.lineTo(0, r * 0.5);
        ctx.lineTo(-r * 0.15, r * 0.9);
        ctx.lineTo(-r * 0.35, r * 0.2);
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = shade(color, 40);
        ctx.beginPath();
        ctx.moveTo(r * 0.2, 0);
        ctx.lineTo(r * 1.1, r * 0.5);
        ctx.lineTo(r * 0.15, r * 0.35);
        ctx.closePath();
        ctx.fill();
        ctx.beginPath();
        ctx.moveTo(-r * 0.2, 0);
        ctx.lineTo(-r * 1.1, r * 0.5);
        ctx.lineTo(-r * 0.15, r * 0.35);
        ctx.closePath();
        ctx.fill();
    }

    _drawRingShip(ctx, r, color, t) {
        ctx.strokeStyle = color;
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.ellipse(0, 0, r * 1.15, r * 0.55, 0, 0, Math.PI * 2);
        ctx.stroke();
        const g = ctx.createRadialGradient(0, 0, 0, 0, 0, r * 0.65);
        g.addColorStop(0, '#fff');
        g.addColorStop(0.5, color);
        g.addColorStop(1, shade(color, -40));
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(0, 0, r * 0.55, 0, Math.PI * 2);
        ctx.fill();
        for (let i = 0; i < 3; i++) {
            const a = t / 280 + (i / 3) * Math.PI * 2;
            ctx.fillStyle = '#fff';
            ctx.beginPath();
            ctx.arc(Math.cos(a) * r * 1.15, Math.sin(a) * r * 0.55, 2.5, 0, Math.PI * 2);
            ctx.fill();
        }
    }

    _drawFinShip(ctx, r, color) {
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.moveTo(0, -r * 1.25);
        ctx.quadraticCurveTo(r * 0.9, 0, 0, r * 0.9);
        ctx.quadraticCurveTo(-r * 0.9, 0, 0, -r * 1.25);
        ctx.fill();
        ctx.fillStyle = shade(color, 25);
        ctx.beginPath();
        ctx.moveTo(r * 0.3, r * 0.1);
        ctx.lineTo(r * 1.15, r * 0.55);
        ctx.lineTo(r * 0.25, r * 0.45);
        ctx.closePath();
        ctx.fill();
        ctx.beginPath();
        ctx.moveTo(-r * 0.3, r * 0.1);
        ctx.lineTo(-r * 1.15, r * 0.55);
        ctx.lineTo(-r * 0.25, r * 0.45);
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = '#fff';
        ctx.beginPath();
        ctx.ellipse(0, -r * 0.2, r * 0.18, r * 0.28, 0, 0, Math.PI * 2);
        ctx.fill();
    }

    _drawLeafShip(ctx, r, color) {
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.moveTo(0, -r * 1.2);
        ctx.bezierCurveTo(r * 0.9, -r * 0.3, r * 0.7, r * 0.8, 0, r);
        ctx.bezierCurveTo(-r * 0.7, r * 0.8, -r * 0.9, -r * 0.3, 0, -r * 1.2);
        ctx.fill();
        ctx.strokeStyle = shade(color, 40);
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(0, -r * 0.9);
        ctx.lineTo(0, r * 0.7);
        ctx.stroke();
        ctx.fillStyle = '#0a1a0a';
        ctx.beginPath();
        ctx.arc(0, -r * 0.15, r * 0.2, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = '#8f8';
        ctx.beginPath();
        ctx.arc(0, -r * 0.2, r * 0.08, 0, Math.PI * 2);
        ctx.fill();
    }

    _drawMechShip(ctx, r, color) {
        ctx.fillStyle = shade(color, -25);
        ctx.beginPath();
        for (let i = 0; i < 6; i++) {
            const a = (i / 6) * Math.PI * 2 - Math.PI / 6;
            const px = Math.cos(a) * r;
            const py = Math.sin(a) * r;
            if (i === 0) ctx.moveTo(px, py);
            else ctx.lineTo(px, py);
        }
        ctx.closePath();
        ctx.fill();
        ctx.strokeStyle = color;
        ctx.lineWidth = 2.5;
        ctx.stroke();
        ctx.fillStyle = color;
        ctx.fillRect(-r * 1.05, -r * 0.15, r * 0.35, r * 0.3);
        ctx.fillRect(r * 0.7, -r * 0.15, r * 0.35, r * 0.3);
        ctx.fillStyle = '#fff';
        ctx.beginPath();
        ctx.arc(0, 0, r * 0.25, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = '#0a1020';
        ctx.beginPath();
        ctx.arc(0, 0, r * 0.12, 0, Math.PI * 2);
        ctx.fill();
    }

    _drawBrute(ctx, r, color) {
        ctx.fillStyle = shade(color, -25);
        ctx.beginPath();
        for (let i = 0; i < 6; i++) {
            const a = (i / 6) * Math.PI * 2 - Math.PI / 6;
            const px = Math.cos(a) * r;
            const py = Math.sin(a) * r;
            if (i === 0) ctx.moveTo(px, py);
            else ctx.lineTo(px, py);
        }
        ctx.closePath();
        ctx.fill();
        ctx.strokeStyle = color;
        ctx.lineWidth = 2.5;
        ctx.stroke();
        ctx.fillStyle = color;
        ctx.globalAlpha = 0.55;
        for (let i = 0; i < 3; i++) {
            const a = (i / 3) * Math.PI * 2 + this.animT * 0.3;
            ctx.beginPath();
            ctx.arc(Math.cos(a) * r * 0.35, Math.sin(a) * r * 0.35, r * 0.28, 0, Math.PI * 2);
            ctx.fill();
        }
        ctx.globalAlpha = 1;
        ctx.fillStyle = '#fff';
        ctx.beginPath();
        ctx.arc(0, 0, r * 0.22, 0, Math.PI * 2);
        ctx.fill();
    }

    _drawSpiker(ctx, r, color, t = 0) {
        const extend = 1 + Math.sin(t / 140 + this.pulsePhase) * 0.12;
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(0, 0, r * 0.55, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = shade(color, 40);
        for (let i = 0; i < 6; i++) {
            const a = (i / 6) * Math.PI * 2 + this.spin * 0.2;
            ctx.beginPath();
            ctx.moveTo(Math.cos(a - 0.28) * r * 0.45, Math.sin(a - 0.28) * r * 0.45);
            ctx.lineTo(Math.cos(a) * (r + 8) * extend, Math.sin(a) * (r + 8) * extend);
            ctx.lineTo(Math.cos(a + 0.28) * r * 0.45, Math.sin(a + 0.28) * r * 0.45);
            ctx.closePath();
            ctx.fill();
        }
        ctx.fillStyle = '#fff';
        ctx.beginPath();
        ctx.arc(0, 0, r * 0.18, 0, Math.PI * 2);
        ctx.fill();
    }

    _drawOrbiter(ctx, r, color, t) {
        ctx.strokeStyle = color;
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.arc(0, 0, r * 1.05, 0, Math.PI * 2);
        ctx.stroke();
        const g = ctx.createRadialGradient(0, 0, 0, 0, 0, r * 0.7);
        g.addColorStop(0, '#fff');
        g.addColorStop(0.5, color);
        g.addColorStop(1, shade(color, -40));
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(0, 0, r * 0.65, 0, Math.PI * 2);
        ctx.fill();
        for (let i = 0; i < 3; i++) {
            const a = t / 220 + (i / 3) * Math.PI * 2;
            ctx.fillStyle = '#fff';
            ctx.beginPath();
            ctx.arc(Math.cos(a) * r * 1.05, Math.sin(a) * r * 1.05, 3.2, 0, Math.PI * 2);
            ctx.fill();
        }
    }

    _drawHpBar(ctx, barWidth, barHeight, y) {
        ctx.globalAlpha = 1;
        ctx.shadowBlur = 0;
        ctx.fillStyle = 'rgba(0,0,0,0.65)';
        ctx.fillRect(this.x - barWidth / 2, y, barWidth, barHeight);
        const ratio = Math.max(0, this.hp / this.maxHp);
        ctx.fillStyle = this.isBoss ? '#ff2244' : ratio > 0.5 ? '#33ff88' : ratio > 0.25 ? '#ffcc00' : '#ff4444';
        ctx.fillRect(this.x - barWidth / 2, y, barWidth * ratio, barHeight);
        ctx.strokeStyle = 'rgba(255,255,255,0.25)';
        ctx.lineWidth = 1;
        ctx.strokeRect(this.x - barWidth / 2, y, barWidth, barHeight);
    }

    takeDamage(damage) {
        this.hp -= damage;
        this.hitFlash = 6;
        return this.hp <= 0;
    }
}

export class Boss extends Enemy {
    constructor(x, y, theme = null) {
        super(x, y, theme);
        this._applyBossStats(theme);
    }

    _applyBossStats(theme = null) {
        this.isBoss = true;
        this.variant = 'boss';
        this.shipSilhouette = 'boss';
        this.radius = 42;
        this.speed = 0.7;
        this.hp = 300;
        this.maxHp = 300;
        this.damage = 25;
        const faction = theme?.faction || this.faction || getFaction('barren');
        this.faction = faction;
        this.color = faction.bossColor || '#ff0066';
        this.enemyShip = faction.enemyShip || 'drone';
        this.hasPulse = true;
        this.pulsePhase = 0;
        this.shootInterval = 70;
        this.shootRange = 420;
        const danger = theme?.danger ?? 0.5;
        const scale = combatScale(faction, danger);
        this.hp = Math.round(300 * scale.hpMul * 1.15);
        this.maxHp = this.hp;
        this.damage = Math.round(25 * scale.dmgMul);
        this.speed *= scale.speedMul * 0.9;
        this.trail = [];
    }

    reset(x, y, theme = null) {
        super.reset(x, y, theme);
        this._applyBossStats(theme);
    }

    canShoot(player) {
        if (this.shootCooldown > 0) return false;
        const dx = player.x - this.x;
        const dy = player.y - this.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        return dist < this.shootRange;
    }

    update(player) {
        super.update(player);
        // Boss always faces player more aggressively
        const dx = player.x - this.x;
        const dy = player.y - this.y;
        this.angle = Math.atan2(dy, dx);
        this.spin += 0.03;
        this.thrust = 0.8;
    }

    draw(ctx) {
        ctx.save();
        const t = Date.now();
        const pulse = Math.sin(t / 200 + this.pulsePhase);
        const absPulse = Math.abs(pulse);
        const bodyColor = this.hitFlash > 0 ? '#ffffff' : this.color;
        const label = (this.faction?.label || 'BOSS').toUpperCase().slice(0, 14);

        // Trails
        for (const p of this.trail) {
            const a = p.life / p.max * 0.5;
            ctx.fillStyle = `rgba(255,100,150,${a})`;
            ctx.beginPath();
            ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
            ctx.fill();
        }

        for (let i = 0; i < 2; i++) {
            const expand = (t / 12 + i * 40) % 50;
            ctx.globalAlpha = (1 - expand / 50) * 0.25;
            ctx.strokeStyle = this.color;
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.arc(this.x, this.y, this.radius + 12 + expand, 0, Math.PI * 2);
            ctx.stroke();
        }

        const amb = ctx.createRadialGradient(this.x, this.y, 0, this.x, this.y, this.radius * 2.8);
        amb.addColorStop(0, this.color + '55');
        amb.addColorStop(1, 'transparent');
        ctx.globalAlpha = 0.8;
        ctx.fillStyle = amb;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius * 2.8, 0, Math.PI * 2);
        ctx.fill();

        ctx.globalAlpha = 1;
        ctx.shadowBlur = 40 + absPulse * 20;
        ctx.shadowColor = this.color;

        ctx.save();
        ctx.translate(this.x, this.y);
        ctx.rotate(t / 900);
        ctx.fillStyle = shade(this.color, 20);
        for (let i = 0; i < 10; i++) {
            const angle = (i / 10) * Math.PI * 2;
            const tip = this.radius + 14 + pulse * 4;
            ctx.beginPath();
            ctx.moveTo(Math.cos(angle - 0.18) * this.radius * 0.9, Math.sin(angle - 0.18) * this.radius * 0.9);
            ctx.lineTo(Math.cos(angle) * tip, Math.sin(angle) * tip);
            ctx.lineTo(Math.cos(angle + 0.18) * this.radius * 0.9, Math.sin(angle + 0.18) * this.radius * 0.9);
            ctx.closePath();
            ctx.fill();
        }

        const g = ctx.createRadialGradient(-this.radius * 0.25, -this.radius * 0.25, 4, 0, 0, this.radius);
        g.addColorStop(0, '#ffffff');
        g.addColorStop(0.25, bodyColor);
        g.addColorStop(1, shade(this.color, -55));
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(0, 0, this.radius, 0, Math.PI * 2);
        ctx.fill();

        ctx.shadowBlur = 20;
        ctx.shadowColor = this.color;
        ctx.fillStyle = '#1a0010';
        ctx.beginPath();
        ctx.arc(0, 0, this.radius * 0.42, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = bodyColor;
        ctx.beginPath();
        ctx.arc(this.radius * 0.08, -this.radius * 0.05, this.radius * 0.18, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = '#fff';
        ctx.beginPath();
        ctx.arc(this.radius * 0.12, -this.radius * 0.08, this.radius * 0.07, 0, Math.PI * 2);
        ctx.fill();

        ctx.rotate(-t / 450);
        ctx.strokeStyle = this.color;
        ctx.lineWidth = 3;
        ctx.shadowBlur = 12;
        ctx.shadowColor = this.color;
        for (let i = 0; i < 8; i++) {
            const a0 = (i / 8) * Math.PI * 2;
            const a1 = a0 + Math.PI / 10;
            ctx.beginPath();
            ctx.arc(0, 0, this.radius * 0.72, a0, a1);
            ctx.stroke();
        }
        ctx.restore();

        const barWidth = this.radius * 3;
        const barHeight = 8;
        ctx.globalAlpha = 1;
        ctx.shadowBlur = 0;
        ctx.fillStyle = 'rgba(0,0,0,0.7)';
        ctx.fillRect(this.x - barWidth / 2, this.y - this.radius - 28, barWidth, barHeight);
        ctx.fillStyle = '#ff2244';
        ctx.fillRect(this.x - barWidth / 2, this.y - this.radius - 28, barWidth * (this.hp / this.maxHp), barHeight);
        ctx.strokeStyle = this.color + 'aa';
        ctx.lineWidth = 1;
        ctx.strokeRect(this.x - barWidth / 2, this.y - this.radius - 28, barWidth, barHeight);

        ctx.font = 'bold 12px Orbitron, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        const tw = ctx.measureText(label).width;
        ctx.fillStyle = 'rgba(20,0,10,0.8)';
        ctx.strokeStyle = this.color;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.roundRect(this.x - tw / 2 - 10, this.y - this.radius - 52, tw + 20, 20, 6);
        ctx.fill();
        ctx.stroke();
        ctx.fillStyle = this.color;
        ctx.shadowBlur = 10;
        ctx.shadowColor = this.color;
        ctx.fillText(label, this.x, this.y - this.radius - 42);

        ctx.shadowBlur = 0;
        ctx.fillStyle = this.color;
        const cy = this.y - this.radius - 62;
        ctx.beginPath();
        ctx.moveTo(this.x - 14, cy + 8);
        ctx.lineTo(this.x - 14, cy - 2);
        ctx.lineTo(this.x - 7, cy + 4);
        ctx.lineTo(this.x, cy - 8);
        ctx.lineTo(this.x + 7, cy + 4);
        ctx.lineTo(this.x + 14, cy - 2);
        ctx.lineTo(this.x + 14, cy + 8);
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = '#fff';
        ctx.beginPath();
        ctx.arc(this.x, cy - 2, 2.5, 0, Math.PI * 2);
        ctx.fill();

        ctx.restore();
    }
}

function shade(hex, amount) {
    const c = (hex || '#ff0066').replace('#', '');
    if (c.length !== 6) return hex;
    const num = parseInt(c, 16);
    let r = (num >> 16) + amount;
    let g = ((num >> 8) & 0xff) + amount;
    let b = (num & 0xff) + amount;
    r = Math.max(0, Math.min(255, r));
    g = Math.max(0, Math.min(255, g));
    b = Math.max(0, Math.min(255, b));
    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}
